import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Paperclip, Upload, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function Thumb({ att, onDelete, canDelete }) {
  const [url, setUrl] = useState(null);
  const isImg = (att.content_type || "").startsWith("image/");
  useEffect(() => {
    let made;
    if (isImg) {
      api.get(`/attachments/${att.id}/download`, { responseType: "blob" })
        .then((r) => { made = URL.createObjectURL(r.data); setUrl(made); })
        .catch(() => {});
    }
    return () => { if (made) URL.revokeObjectURL(made); };
  }, [att.id]);

  const open = async () => {
    const r = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };

  return (
    <div className="group relative rounded-md border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid="attachment-item">
      <button onClick={open} className="block w-full text-left">
        {isImg && url ? (
          <img src={url} alt={att.original_filename} className="h-24 w-full object-cover" />
        ) : (
          <div className="h-24 w-full flex items-center justify-center bg-slate-50">
            <FileText size={26} className="text-slate-400" />
          </div>
        )}
        <div className="px-2 py-1.5">
          <p className="text-xs text-slate-700 truncate">{att.original_filename}</p>
          <p className="text-[10px] text-slate-400">{(att.size / 1024).toFixed(0)} KB · {att.uploaded_by_name}</p>
        </div>
      </button>
      {canDelete && (
        <button onClick={() => onDelete(att.id)} data-testid="delete-attachment-btn"
          className="absolute top-1 right-1 h-6 w-6 rounded bg-white/90 border border-slate-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Trash2 size={12} className="text-red-500" />
        </button>
      )}
    </div>
  );
}

export default function Attachments({ ticketId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = () => api.get(`/tickets/${ticketId}/attachments`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [ticketId]);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/tickets/${ticketId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/attachments/${id}`);
    load();
  };

  return (
    <Card className="p-5 border-slate-200">
      <p className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Paperclip size={16} style={{ color: "var(--tenant-primary)" }} /> Attachments ({items.length})
      </p>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="attachment-dropzone"
        className={`rounded-md border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${
          drag ? "border-orange-400 bg-orange-50" : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <Upload size={20} className="mx-auto text-slate-400 mb-1.5" />
        <p className="text-sm text-slate-500">{uploading ? "Uploading…" : "Drop screenshots or files here, or click to browse"}</p>
        <input ref={inputRef} type="file" multiple className="hidden" data-testid="attachment-input"
          onChange={(e) => upload(e.target.files)} />
      </div>
      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          {items.map((a) => (
            <Thumb key={a.id} att={a} onDelete={del} canDelete={user.role !== "client" || a.uploaded_by === user.id} />
          ))}
        </div>
      )}
    </Card>
  );
}
